import React, { useEffect, useState } from 'react';
import { Modal, Checkbox, List, Button, Row, Col, Select, DatePicker, Space, Typography, Input, Alert, Spin } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { fetchShopifyStores } from '../../shopify/ShopifySettings/actions';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Text } = Typography;

const SyncShopifyOrdersModal = ({ visible, onOk, onCancel, loading }) => {
  const dispatch = useDispatch();
  const { shopifyStores, loading: storesLoading } = useSelector(state => state.shopifySettings || { shopifyStores: [], loading: false });
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [syncPeriod, setSyncPeriod] = useState('last7Days');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'days'),
    dayjs()
  ]);
  const [shopifyOrderNames, setShopifyOrderNames] = useState('');

  useEffect(() => {
    if (visible) {
      // Fetch Shopify stores when modal opens
      dispatch(fetchShopifyStores());
    }
  }, [visible, dispatch]);

  useEffect(() => {
    if (visible && shopifyStores && shopifyStores.length > 0) {
      // If specific order names provided, only allow single store selection
      if (shopifyOrderNames.trim()) {
        setSelectedStoreIds([shopifyStores[0].id]);
      } else {
        setSelectedStoreIds(shopifyStores.map(store => store.id)); // Default select all
      }
    }
  }, [visible, shopifyStores, shopifyOrderNames]);

  const onChange = (checkedValues) => {
    // If specific order names provided, only allow single selection
    if (shopifyOrderNames.trim()) {
      setSelectedStoreIds(checkedValues.slice(-1)); // Keep only the last selected
    } else {
      setSelectedStoreIds(checkedValues);
    }
  };
  
  const handleSyncPeriodChange = (value) => {
    setSyncPeriod(value);
    
    // Update default date range based on selection
    if (value === 'last7Days') {
      setDateRange([dayjs().subtract(7, 'days'), dayjs()]);
    } else if (value === 'last30Days') {
      setDateRange([dayjs().subtract(30, 'days'), dayjs()]);
    } else if (value === 'lastMonth') {
      setDateRange([
        dayjs().subtract(1, 'month').startOf('month'),
        dayjs().subtract(1, 'month').endOf('month')
      ]);
    }
  };
  
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const handleOrderNamesChange = (e) => {
    const value = e.target.value;
    setShopifyOrderNames(value);
    
    // If order names entered, restrict to single store selection
    if (value.trim() && selectedStoreIds.length > 1) {
      setSelectedStoreIds(selectedStoreIds.slice(0, 1));
    }
  };

  // Parse and validate Shopify order names
  const parseOrderNames = () => {
    if (!shopifyOrderNames.trim()) return [];
    
    return shopifyOrderNames
      .split(/[\n,\s]+/)
      .map(name => name.trim())
      .filter(name => name.length > 0)
      .map(name => name.startsWith('#') ? name : `#${name}`) // Auto-add # prefix
      .slice(0, 50); // Limit to 50
  };

  const validOrderNames = parseOrderNames();
  const hasOrderNames = validOrderNames.length > 0;

  const handleOk = () => {
    const params = {
      storeIds: selectedStoreIds
    };

    if (hasOrderNames) {
      // Sync specific orders - use wide date range
      params.shopifyOrderNames = validOrderNames;
      params.syncPeriod = 'custom';
      params.startDate = null;
      params.endDate = null;
    } else {
      // Sync by date range
      params.syncPeriod = syncPeriod;
      
      if (syncPeriod === 'custom' && dateRange && dateRange.length === 2) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
    }

    onOk(params);
  };

  return (
    <Modal
      title="Đồng bộ đơn hàng từ Shopify"
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Đồng bộ"
      cancelText="Hủy"
      confirmLoading={loading}
      width={700}
      okButtonProps={{ disabled: selectedStoreIds.length === 0 }}
    >
      {storesLoading || !shopifyStores || shopifyStores.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Đang tải danh sách stores..." />
        </div>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Specific Order Names Input */}
          <div>
          <Text strong>Tên đơn hàng cụ thể (tùy chọn):</Text>
          <TextArea
            style={{ marginTop: 8 }}
            rows={4}
            placeholder="Nhập danh sách tên đơn hàng (cách nhau bằng dấu phẩy hoặc xuống dòng). Tối đa 50 đơn.&#10;Ví dụ: #1001, #1002, 1003"
            value={shopifyOrderNames}
            onChange={handleOrderNamesChange}
          />
          {hasOrderNames && (
            <Alert
              message={`Đã nhập ${validOrderNames.length} đơn hàng${validOrderNames.length === 50 ? ' (tối đa)' : ''}`}
              type="info"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}
          {hasOrderNames && selectedStoreIds.length > 1 && (
            <Alert
              message="Khi sync đơn hàng cụ thể, chỉ được chọn 1 store"
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        {/* Store Selection */}
        <div>
          <Text strong>Chọn Shopify Stores:</Text>
          <Checkbox.Group 
            style={{ width: '100%', marginTop: 8 }} 
            value={selectedStoreIds}
            onChange={onChange}
          >
            <Row>
              {shopifyStores && shopifyStores.length > 0 ? (
                shopifyStores.map((store) => (
                  <Col span={24} key={store.id} style={{ marginBottom: 8 }}>
                    <Checkbox 
                      value={store.id}
                      disabled={hasOrderNames && selectedStoreIds.length > 0 && !selectedStoreIds.includes(store.id)}
                    >
                      {store.storeName} ({store.shopifyDomain})
                    </Checkbox>
                  </Col>
                ))
              ) : (
                <Col span={24}>
                  <Text type="secondary">Không có Shopify store nào</Text>
                </Col>
              )}
            </Row>
          </Checkbox.Group>
        </div>

        {/* Time Period Selection - Only show if no specific order names */}
        {!hasOrderNames && (
          <>
            <div>
              <Text strong>Khoảng thời gian:</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={syncPeriod}
                onChange={handleSyncPeriodChange}
              >
                <Option value="last7Days">7 ngày gần nhất</Option>
                <Option value="last30Days">30 ngày gần nhất</Option>
                <Option value="lastMonth">Tháng trước</Option>
                <Option value="custom">Tùy chỉnh</Option>
              </Select>
            </div>

            {syncPeriod === 'custom' && (
              <div>
                <Text strong>Chọn khoảng ngày:</Text>
                <RangePicker
                  style={{ width: '100%', marginTop: 8 }}
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                  allowClear={false}
                />
              </div>
            )}
          </>
        )}

        {/* Summary */}
        <Alert
          message={
            hasOrderNames
              ? `Sẽ sync ${validOrderNames.length} đơn hàng cụ thể từ ${selectedStoreIds.length} store(s)`
              : `Sẽ sync đơn hàng từ ${selectedStoreIds.length} store(s) trong khoảng ${
                  syncPeriod === 'custom' && dateRange?.length === 2
                    ? `${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`
                    : syncPeriod === 'last7Days'
                    ? '7 ngày gần nhất'
                    : syncPeriod === 'last30Days'
                    ? '30 ngày gần nhất'
                    : 'tháng trước'
                }`
          }
          type="info"
          showIcon
        />
      </Space>
      )}
    </Modal>
  );
};

export default SyncShopifyOrdersModal;
